import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  SearchOutlined,
  AddCircleOutline,
  Visibility,
  Edit,
  CalendarTodayOutlined,
} from '@mui/icons-material';

const LegalList = ({ type, title, description }) => {
  const server_url = process.env.REACT_APP_API_URI;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${server_url}/admin/legal/${type}`);
        const list = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        const sorted = [...list].sort((a, b) => {
          const aDate = new Date(
            a?.updatedAt || a?.effectiveAt || a?.createdAt || 0,
          ).getTime();
          const bDate = new Date(
            b?.updatedAt || b?.effectiveAt || b?.createdAt || 0,
          ).getTime();
          return bDate - aDate;
        });
        setItems(sorted);
        setError('');
      } catch (err) {
        setError('Failed to load documents.');
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [server_url, type]);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const query = searchTerm.toLowerCase();
    return items.filter(
      (item) =>
        item?.title?.toLowerCase().includes(query) ||
        item?.version?.toLowerCase().includes(query),
    );
  }, [items, searchTerm]);

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const latestEffective = items[0]?.effectiveAt || items[0]?.updatedAt;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          {title}
        </h1>
        <p className="text-gray-600">{description}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Total Versions
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {items.length}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <CalendarTodayOutlined className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-emerald-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Latest Effective
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {latestEffective ? formatDate(latestEffective) : '—'}
              </h3>
            </div>
            <div className="bg-emerald-100 p-3 rounded-full">
              <CalendarTodayOutlined className="text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or version..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <Link to={`/admin/legal/${type}/new`} className="w-full">
            <button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg">
              <AddCircleOutline />
              Create Version
            </button>
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">{error}</div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No documents found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Effective
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-800">
                        {item.title || title}
                      </div>
                      <div className="text-xs text-gray-500">{item._id}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.version || '1.0'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(item.effectiveAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(item.updatedAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Link
                          to={`/admin/legal/${type}/${item._id}`}
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <Visibility fontSize="small" />
                          View
                        </Link>
                        <Link
                          to={`/admin/legal/${type}/${item._id}`}
                          className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-800"
                        >
                          <Edit fontSize="small" />
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalList;
